import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, MapPin, Camera, Smartphone } from 'lucide-react';

const AuthPage = () => {
  const { login } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '' });

  // Handle Emergent Auth Google OAuth callback
  useEffect(() => {
    const handleGoogleCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setIsLoading(true);
        
        // Extract session_id from URL fragment
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          // Send session_id to backend to exchange for user data
          const response = await axios.post(
            '/auth/session',
            { session_id: sessionId },
            { withCredentials: true } // Important for cookie handling
          );
          
          // Login user (session_token is now in httpOnly cookie)
          login(null, response.data.user); // Don't store token in localStorage for Google auth
          
          // Clean URL fragment
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          toast.error('Google authentication failed');
          console.error('Google auth error:', error);
          window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    handleGoogleCallback();
  }, [login]);

  const handleGoogleLogin = () => {
    // Redirect directly to Emergent Auth using dynamic origin
    const redirectUrl = window.location.origin + '/auth'; // Redirect back to auth page
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post('/auth/login', loginData);
      login(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post('/auth/register', registerData);
      login(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-surface">
      {/* Left side - Auth form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Logo - visible on all screens */}
          <div className="mb-8 text-center lg:text-left">
            <img 
              src="https://customer-assets.emergentagent.com/job_0639c081-d9f0-47e7-918b-f72657c5b82c/artifacts/6zzokka9_boredom%20buster%20logo.svg" 
              alt="BoredomBuster" 
              className="h-12 w-auto mx-auto lg:mx-0 mb-2"
            />
          </div>

          {/* Title */}
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-3xl sm:text-4xl font-bold text-grayDark mb-2 capitalize"
          >
            {activeTab === 'login' ? 'sign in' : 'sign up'}
          </motion.h1>

          {/* Tab switcher - styled as text links */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray">
            <span>
              {activeTab === 'login' ? "Don't have an account?" : 'Already have an account?'}
            </span>
            <button
              onClick={() => setActiveTab(activeTab === 'login' ? 'register' : 'login')}
              className="text-primaryDark hover:text-primary-dark hover:underline font-bold transition-colors"
            >
              {activeTab === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </div>

          {activeTab === 'login' && (
            <motion.form 
              key="login-form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleLogin} 
              className="space-y-5"
            >
              {/* Google Login */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full h-12 bg-white hover:bg-surface text-grayDark border-2 border-grayLight font-bold rounded-lg shadow-soft flex items-center justify-center gap-3"
                disabled={isLoading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-grayLight"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-surface px-4 text-gray">Or with email</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-sm font-semibold text-grayDark">
                  Your Email
                </Label>
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="h-12 rounded-lg border-2 border-grayLight focus:border-primary focus:ring-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-sm font-semibold text-grayDark">
                  Password
                </Label>
                <Input
                  id="login-password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="h-12 rounded-lg border-2 border-grayLight focus:border-primary focus:ring-primary"
                />
              </div>

              <Button
                data-testid="login-submit-btn"
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primaryDark font-bold rounded-lg shadow-soft mt-6"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </motion.form>
          )}

          {activeTab === 'register' && (
            <motion.form
              key="register-form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleRegister}
              className="space-y-5"
            >
              {/* Google Sign Up */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full h-12 bg-white hover:bg-surface text-grayDark border-2 border-grayLight font-bold rounded-lg shadow-soft flex items-center justify-center gap-3"
                disabled={isLoading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-grayLight"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-surface px-4 text-gray">Or with email</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-username" className="text-sm font-semibold text-grayDark">
                  Your Name
                </Label>
                <Input
                  id="register-username"
                  data-testid="register-username-input"
                  type="text"
                  placeholder="John Doe"
                  value={registerData.username}
                  onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                  required
                  className="h-12 rounded-lg border-2 border-grayLight focus:border-primary focus:ring-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-email" className="text-sm font-semibold text-grayDark">
                  Your Email
                </Label>
                <Input
                  id="register-email"
                  data-testid="register-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  required
                  className="h-12 rounded-lg border-2 border-grayLight focus:border-primary focus:ring-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-password" className="text-sm font-semibold text-grayDark">
                  Password
                </Label>
                <Input
                  id="register-password"
                  data-testid="register-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  required
                  className="h-12 rounded-lg border-2 border-grayLight focus:border-primary focus:ring-primary"
                />
              </div>

              <Button
                data-testid="register-submit-btn"
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primaryDark font-bold rounded-lg shadow-soft mt-6"
                disabled={isLoading}
              >
                {isLoading ? 'Creating account...' : 'Register'}
              </Button>
            </motion.form>
          )}
        </motion.div>
      </div>

      {/* Right side - Features */}
      <div className="hidden lg:flex lg:flex-1 bg-white items-center justify-center p-12 relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="w-full max-w-lg space-y-8"
        >
          {/* Header */}
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-grayDark mb-3">
              Never Be Bored Again
            </h2>
            <p className="text-gray text-lg leading-relaxed">
              Discover activities tailored to your time, location, and mood. Share your adventures with a vibrant community.
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-6">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex gap-4 items-start group"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Clock className="w-6 h-6 text-primaryDark" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-bold text-grayDark mb-1 text-lg">
                  Fit Your Schedule
                </h3>
                <p className="text-gray text-sm leading-relaxed">
                  Whether you have 5 minutes or 2 hours, find the perfect activity to fill the gap instantly.
                </p>
              </div>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex gap-4 items-start group"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <MapPin className="w-6 h-6 text-primaryDark" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-bold text-grayDark mb-1 text-lg">
                  Go Hyper-Local
                </h3>
                <p className="text-gray text-sm leading-relaxed">
                  Don't settle for generic advice. Join city hubs to discover real-world activities happening near you.
                </p>
              </div>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex gap-4 items-start group"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Camera className="w-6 h-6 text-primaryDark" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-bold text-grayDark mb-1 text-lg">
                  See It, Snap It
                </h3>
                <p className="text-gray text-sm leading-relaxed">
                  Browse a visual feed of what others are doing and use the instant camera mode to share your own adventures.
                </p>
              </div>
            </motion.div>

            {/* Feature 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="flex gap-4 items-start group"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Smartphone className="w-6 h-6 text-primaryDark" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-bold text-grayDark mb-1 text-lg">
                  No Download Needed
                </h3>
                <p className="text-gray text-sm leading-relaxed">
                  Experience the smooth, tactile feel of a native mobile app directly in your browser—zero install required.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Subtle decorations */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-10 right-10 w-32 h-32 bg-primary/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-10 left-10 w-40 h-40 bg-primary/15 rounded-full blur-3xl"
        />
      </div>
    </div>
  );
};

export default AuthPage;
